import { ReactNode } from 'react';
import { FormControl, Radio } from 'react-bootstrap';
import Tooltip from 'components/tooltip/Tooltip';
import GenericFormField from '../genericFormField/GenericFormField';
import {
  FieldRendererProps,
  GenericFormFieldBaseProps,
} from '../genericFormField/GenericFormField.types';
import { RadioGroupContainer } from './RadioGroupField.styles';

interface Props extends Omit<GenericFormFieldBaseProps, 'fieldRenderer'> {
  /**
   * If true, displays the radio inputs horizontally instead of vertically (default).
   */
  horizontal?: boolean;
  options: {
    value: number | string;
    label: ReactNode;
    tooltipText?: ReactNode;
  }[];
  onChangeHandler?: (event: React.FormEvent<FormControl>) => void;
}

const RadioGroupField = ({
  name,
  id = name,
  options,
  horizontal,
  onChangeHandler,
  disabled,
  ...rest
}: Props) => {
  const fieldRenderer = ({
    field: { value: currentValue, ref, onChange, ...restFieldProps },
  }: FieldRendererProps) => (
    <RadioGroupContainer $horizontal={horizontal}>
      {options.map(({ label, value, tooltipText }) => {
        const radio = (
          <Radio
            id={`${id}-${value}`}
            key={`${id}-${value}`}
            data-testid={name}
            // @ts-expect-error TS2783 [STRICT-MIGRATION] Temporarily suppressing strict type checking - should be fixed when this code is next modified
            name={name}
            value={value}
            checked={value === currentValue}
            inputRef={ref}
            onChange={(...args) => {
              onChange(...args);
              onChangeHandler?.(...args);
            }}
            disabled={disabled}
            {...restFieldProps}
          >
            {label}
          </Radio>
        );
        return tooltipText ? (
          <Tooltip
            text={tooltipText}
            key={`${id}-${value}`}
            id={`${id}-${value}`}
          >
            {radio}
          </Tooltip>
        ) : (
          radio
        );
      })}
    </RadioGroupContainer>
  );

  return (
    <GenericFormField
      id={id}
      name={name}
      fieldRenderer={fieldRenderer}
      {...rest}
    />
  );
};

RadioGroupField.defaultProps = {
  horizontal: undefined,
  onChangeHandler: undefined,
};

export default RadioGroupField;
